import React from "react";
import { Divider } from "../../atoms/divider/divider";
import { Link } from "../../atoms/link/link";
import { Text, plainText } from "../../atoms/text/text";
import { LINK_POSITION_BODY } from "../../services/analytics";
import { isSafeHref } from "../../services/safe-href";
import { GenrePack } from "./genre-pack";
import type { LocalityKind } from "./locality-badge";
import type { LineupPack } from "./channel-lineup.types";
import styles from "./genre-pack-list.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * Genre Packs view (GP1-GP14, GP17). Server Component: it places the packs one below the other in
 * the order it is handed, draws the divider between them, and carries the view's own failure state.
 */

/**
 * GP9 / GP13 — what one pack carries that the others do not. Keyed by `billingProductCode` at the
 * call site rather than decided here: which pack is MyHome Team is a resolver fact, and
 * DATA-CONTRACT §3 forbids an organism seeing a code-matching rule.
 */
export interface GenrePackSlots {
  /** GP13 — that pack's own ZIP control. */
  zipSlot?: React.ReactNode;
  /** GP9 — that pack's footer link and the pop-up it opens. */
  footerSlot?: React.ReactNode;
}

export interface GenrePackListProps {
  packs: LineupPack[];
  /** GP5, content-managed and shared by every pack. Shows only when written. */
  subscriptions_heading_content?: string;
  /** GP6, content-managed and shared by every pack. Shows only when written. */
  channels_heading_content?: string;
  /** GP12 — the collapsed pack's toggle wording. */
  show_more_label?: string;
  /** GP12 — the expanded pack's toggle wording. */
  show_less_label?: string;
  /** GP7 / GR4 — the badge wording per kind. Absent or blank, that kind draws no badge. */
  local_badge_label?: string;
  rsn_badge_label?: string;
  national_badge_label?: string;
  /** GP7 — the badge ⓘ text per kind. Absent or blank, no ⓘ. */
  local_badge_tooltip_content?: string;
  rsn_badge_tooltip_content?: string;
  national_badge_tooltip_content?: string;
  /**
   * GP9 / GP13 — per-pack slots, keyed by `billingProductCode`. A code with no entry gets neither,
   * which is every pack but MyHome Team today.
   */
  packSlots?: Record<string, GenrePackSlots>;
  /**
   * GP11 — "if the Genre Packs view can't load". `"error"` replaces the whole list with the message
   * and the PDF link; `packs` is ignored in that state.
   */
  status?: "ready" | "error";
  /** GP11, content-managed. Defaults to the audit's own wording. */
  error_message_content?: string;
  /** GP11 — "a DIRECTV via Internet channel list PDF link is also shown".
   *  Absent draws no link, because a link with nowhere to go is not one. */
  error_pdf_url?: string;
  /** GP11, content-managed. Defaults to the audit's own wording. */
  error_pdf_link_label?: string;
  /** Layout only. */
  className?: string;
}

/** Built only for the kinds the caller actually supplied, so `undefined`
 *  (take the default) stays distinguishable from `""` (render no ⓘ). */
function wordingFor(
  local: string | undefined,
  rsn: string | undefined,
  national: string | undefined
): Partial<Record<LocalityKind, string>> | undefined {
  const wording: Partial<Record<LocalityKind, string>> = {};
  if (local !== undefined) {
    wording.local = local;
  }
  if (rsn !== undefined) {
    wording.rsn = rsn;
  }
  if (national !== undefined) {
    wording.national = national;
  }
  return Object.keys(wording).length > 0 ? wording : undefined;
}

function GenrePackListImpl({
  packs,
  subscriptions_heading_content: subscriptionsHeading,
  channels_heading_content: channelsHeading,
  show_more_label: showMoreLabel,
  show_less_label: showLessLabel,
  local_badge_label: localBadgeLabel,
  rsn_badge_label: rsnBadgeLabel,
  national_badge_label: nationalBadgeLabel,
  local_badge_tooltip_content: localBadgeTooltip,
  rsn_badge_tooltip_content: rsnBadgeTooltip,
  national_badge_tooltip_content: nationalBadgeTooltip,
  packSlots,
  status = "ready",
  error_message_content: errorMessageContent,
  error_pdf_url: errorPdfUrl,
  error_pdf_link_label: errorPdfLinkLabel,
  className
}: GenrePackListProps) {
  const classes = [styles.list, className].filter(Boolean).join(" ");

  if (status === "error") {
    // GP11 — the view's own failure. "The rest of the page stays usable" is
    // satisfied by this being the view's slot and nothing above it.
    const message = plainText((errorMessageContent ?? "").trim());
    /* GP11 — judged by `services/safe-href`, not merely by "is it blank". */
    const askedPdfUrl = (errorPdfUrl ?? "").trim();
    const pdfUrl = isSafeHref(askedPdfUrl) ? askedPdfUrl : "";
    const pdfLabel = plainText((errorPdfLinkLabel ?? "").trim());

    return (
      <div className={[classes, styles.error].join(" ")} role="alert">
        <Text as="p" variant="body-2">
          {message}
        </Text>
        {pdfUrl !== "" && pdfLabel !== "" && (
          <Link contrast="low" endIcon href={pdfUrl} linkPosition={LINK_POSITION_BODY}>
            {pdfLabel}
          </Link>
        )}
      </div>
    );
  }

  const badgeLabels = wordingFor(localBadgeLabel, rsnBadgeLabel, nationalBadgeLabel);
  const badgeTooltips = wordingFor(localBadgeTooltip, rsnBadgeTooltip, nationalBadgeTooltip);

  return (
    <div className={classes}>
      {packs.map((pack, index) => {
        /* Own-property lookup, not a bare index: the keys are billing product
           codes off a feed, and a bare index answers `"constructor"` with a
           function where a `GenrePackSlots` is expected. */
        const slots =
          packSlots && Object.prototype.hasOwnProperty.call(packSlots, pack.billingProductCode) ?
            packSlots[pack.billingProductCode] :
            undefined;

        return (
          <React.Fragment key={pack.billingProductCode}>
            <GenrePack
              badgeLabels={badgeLabels}
              badgeTooltips={badgeTooltips}
              channelsHeading={channelsHeading}
              footerSlot={slots?.footerSlot}
              pack={pack}
              showLessLabel={showLessLabel}
              showMoreLabel={showMoreLabel}
              subscriptionsHeading={subscriptionsHeading}
              zipSlot={slots?.zipSlot}
            />
            {/* GP1 — a divider between the packs shown, none after the last. */}
            {index < packs.length - 1 && <Divider className={styles.divider} decorative />}
          </React.Fragment>
        );
      })}
    </div>
  );
}
export const GenrePackList = withSafeRender(GenrePackListImpl, "GenrePackList");

export default GenrePackList;
